import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useRef, useState } from 'react';
import { useStores } from '../../stores';
import { observer } from 'mobx-react';
import {
  buildFusionChartDataSource,
  formatTrendParameters,
  getCostTrend,
  getFusionChartType,
  getTotalMembersApi,
} from '../../core-utils/helper-utils/trendAnalysisHelper';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import Loader from '../../_components/molecules/Loader';
import {
  Breadcrumbs,
  Button,
  Edit,
  NoDataVisual,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { CircularLoader, FusionCharts } from '@HomaHealth/ui-component-library';
import { ICohortDetails } from '../../_components/organisms/CreateNewModalCoreComponent';
import properties from '../../core-utils/properties';
import {
  buildCombinedCSV,
  CohortComparisonLocationProps,
  downloadCSV,
  mergeCohortTrendData,
} from '../../core-utils/helper-utils/cohortComparisonHelper';
import axios from '../../core-utils/axios/axiosSetUp';
import Typography from '@HomaHealth/ui-component-library/lib/_patient-access-components/atoms/Typography';
import { COHORT_COMPARISON } from '../../core-utils/routes';
import { cohortComparisonMessages } from '../../core-utils/messages/cohortComparisonPageConstants';
import PptxGenJS from 'pptxgenjs';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
import { commonDropDownProps } from '../../_components/atoms/ReportsCohortItem';
import { scrollBarStyles } from '../../core-utils/messages/CommonConstants';

const CohortComparisonInternalPage = observer(() => {
  const { classes } = makeStyles()({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      padding: '40px 0px 66px 63px',
    },
    breadcrumbs: {
      flexShrink: 0,
      padding: '12px 20px',
      borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    },
    content: {
      borderTop: `1px solid ${patientAccessTheme.palette.grey[300]}`,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: patientAccessTheme.palette.status.white,
      flex: 1,
      minHeight: 0,
      overflow: 'hidden',
    },
    stickyBar: {
      flexShrink: 0,
      backgroundColor: patientAccessTheme.palette.status.white,
      padding: '20px 20px 0 20px',
      zIndex: 10,
      width: '1200px',
    },
    divider: {
      flexShrink: 0,
      margin: '16px 20px 0 20px',
      borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
      width: '1200px',
    },
    chartsArea: {
      flex: 1,
      overflowY: 'auto',
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      ...scrollBarStyles,
    },
    innerContainer: {
      backgroundColor: patientAccessTheme.palette.grey[100],
      display: 'flex',
      alignItems: 'center',
      padding: '20px',
      gap: '7px',
      height: '40px',
    },
    edit: {
      cursor: 'pointer',
      display: 'flex',
      gap: '8px',
      alignItems: 'center',
    },
    topBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    exportBar: {
      display: 'flex',
      gap: '10px',
      alignItems: 'center',
    },
    exportNote: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(255,255,255,0.6)',
      backdropFilter: 'blur(10px)',
      zIndex: 9999,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    exportText: {
      padding: '20px',
      fontSize: '16px',
      fontFamily: 'Inter',
      fontWeight: 500,
      lineHeight: '16px',
      color: patientAccessTheme.palette.status.white,
      backgroundColor: patientAccessTheme.palette.grey[900],
    },
  })();

  const location: CohortComparisonLocationProps = useLocation();
  const abortController = new AbortController();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const cohortDetailsStore = useStores().cohortDetailsStore;
  const comparison_id = location.state.comparison_id;
  const [charts, setCharts] = useState<
    ({ type: string; dataSource: any } | null)[]
  >([]);
  const [comparisonData, setComparisonData] = useState<any>([]);

  const chartRefs = useRef<(HTMLDivElement | null)[]>([]);

  // ── Bulk export ──────────────────────────────────────────────────────────
  const handleBulkExport = async (format: string) => {
    if (!charts.length) {
      return;
    }

    const trendList: any[] = comparisonData?.trendList ?? [];
    const comparisonName: string =
      comparisonData?.comparisonName ?? 'comparison';

    if (format === 'csv') {
      const resolvedCharts = charts.filter(Boolean) as {
        type: string;
        dataSource: any;
      }[];
      const csv = buildCombinedCSV(resolvedCharts, trendList);
      if (csv) {
        const safeName = comparisonName
          .replace(/\s+/g, '_')
          .replace(/[^a-zA-Z0-9_-]/g, '');
        downloadCSV(csv, `${safeName}_combined.csv`);
      }
      return;
    }

    const captureElement = async (el: HTMLElement) => {
      const hiddenEls = el.querySelectorAll<HTMLElement>(
        '[data-export-hide="true"]',
      );
      hiddenEls.forEach((node) => {
        node.style.visibility = 'hidden';
      });
      const canvas = await html2canvas(el, { scale: 2 });
      hiddenEls.forEach((node) => {
        node.style.visibility = '';
      });
      return canvas;
    };

    if (format === 'pptx') {
      const pptx = new PptxGenJS();
      for (let i = 0; i < chartRefs.current.length; i++) {
        const el = chartRefs.current[i];
        if (!el) {
          continue;
        }
        const canvas = await captureElement(el);
        const imgData = canvas.toDataURL('image/png');
        const slide = pptx.addSlide();
        slide.addImage({ data: imgData, x: 0, y: 0, w: '100%', h: '100%' });
      }
      await pptx.writeFile({ fileName: `${comparisonName}_trends.pptx` });
      return;
    }

    if (format === 'pdf') {
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: [1310, 740],
      });
      for (let i = 0; i < chartRefs.current.length; i++) {
        const el = chartRefs.current[i];
        if (!el) {
          continue;
        }
        if (i !== 0) {
          pdf.addPage();
        }
        const canvas = await captureElement(el);
        const imgData = canvas.toDataURL('image/png');
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      }
      pdf.save(`${comparisonName}_trends.pdf`);
    }
  };

  // ── API calls ────────────────────────────────────────────────────────────
  const getTotalMembers = async (
    updatedCohortDetails: ICohortDetails[],
    selectedComparison: any,
    toggleConditions: any[],
  ) => {
    const startDate =
      selectedComparison?.aggregationParameter === 'paid'
        ? selectedComparison?.paidStartDate
        : selectedComparison?.incurredStartDate;
    const endDate =
      selectedComparison?.aggregationParameter === 'paid'
        ? selectedComparison?.paidEndDate
        : selectedComparison?.incurredEndDate;
    const employerGroup = selectedComparison?.eg_nid;

    let searchParams = '';
    let memberList: string[] = [];
    await updatedCohortDetails?.map((updatedcohort, index) => {
      if (updatedcohort?.property !== '') {
        searchParams +=
          updatedcohort?.category +
          '~' +
          updatedcohort?.condition +
          '~' +
          updatedcohort?.property +
          '^';
      } else {
        let multipleProperties = '';
        updatedcohort?.multipleProperties?.map((multipleProperty) => {
          multipleProperties += multipleProperty + ';';
        });
        multipleProperties = multipleProperties.substring(
          0,
          multipleProperties.length - 1,
        );
        searchParams +=
          updatedcohort?.category +
          '~' +
          updatedcohort?.condition +
          '~' +
          multipleProperties +
          '^';
      }
      if (toggleConditions?.length > 0 && index < toggleConditions?.length) {
        searchParams += toggleConditions?.at(index)?.toggleValue + '^';
      }
    });
    searchParams = searchParams?.substring(0, searchParams.length - 1);
    const response = await getTotalMembersApi(
      startDate,
      endDate,
      employerGroup,
      searchParams,
    );
    const data = response?.data?.data;
    memberList = data?.member_ids;
    cohortDetailsStore.addMemberUniqueIdList(data?.member_ids);
    cohortDetailsStore.addClaimIdList(data?.claim_ids);
    return memberList;
  };

  const buildChartForTrend = async (
    selectedComparison: any,
  ): Promise<{ type: string; dataSource: any }> => {
    const trends = formatTrendParameters(
      selectedComparison.primaryAxisData,
      selectedComparison?.secondaryAxisData,
    );
    const cohortApis = selectedComparison?.cohortDetails.map(
      async (cohort: any) => {
        const memberList = await getTotalMembers(
          cohort?.cohortDetails || [],
          selectedComparison,
          cohort?.toggleConditions || [],
        );
        const data = await getCostTrend(
          {
            paidStartDate: selectedComparison.paidStartDate,
            paidEndDate: selectedComparison.paidEndDate,
            incurredStartDate: selectedComparison.incurredStartDate,
            incurredEndDate: selectedComparison.incurredEndDate,
            aggregationParameter: selectedComparison.aggregationParameter,
            eg_nid: selectedComparison.eg_nid,
            timeGrain: selectedComparison?.timeGrain,
            trends,
            memberList,
          },
          abortController.signal,
        );
        return {
          cohortName: cohort.cohortName,
          cohortColor: cohort.cohortColor,
          data,
        };
      },
    );

    const results = await Promise.all(cohortApis);
    const mergedData = mergeCohortTrendData(results);
    const chartType = getFusionChartType(
      selectedComparison?.isSecondaryAxis,
      selectedComparison?.primaryAxisData,
      selectedComparison?.secondaryAxisData,
    );
    const dataSource = buildFusionChartDataSource(
      mergedData,
      selectedComparison?.primaryAxisData,
      selectedComparison?.secondaryAxisData,
      selectedComparison?.timeGrain,
      chartType,
    );
    return { type: chartType, dataSource };
  };

  const getComparison = async () => {
    try {
      const res = await axios.post(
        properties.aiqBaseUrl +
          properties.deepDiveBaseUrl +
          properties.getComparisonById,
        { comparison_id },
      );
      const data = res?.data?.data || [];
      setComparisonData(data);

      const trendList: any[] = data?.trendList ?? [];
      setCharts(new Array(trendList.length).fill(null));
      await Promise.allSettled(
        trendList.map(async (trend: any, index: number) => {
          try {
            const chart = await buildChartForTrend({ ...data, ...trend });
            setCharts((prev) => {
              const next = [...prev];
              next[index] = chart;
              return next;
            });
          } catch (error) {
            console.error(
              `Error building chart for trend at index ${index}:`,
              error,
            );
          }
        }),
      );
    } catch (error) {
      console.error('Error fetching cohort comparisons:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    getComparison();
    return () => {
      abortController.abort();
    };
  }, [comparison_id]);

  const breadcrumbsItemsList = [
    [
      'text',
      'Cohort Comparison',
      { color: patientAccessTheme.palette.primary[500], fontWeight: 500 },
      () => {
        navigate(COHORT_COMPARISON);
      },
    ],
    [
      'text',
      `${comparisonData?.comparisonName}`,
      { color: patientAccessTheme.palette.text.highemphasis, fontWeight: 600 },
    ],
  ];

  const ExportBar = () => {
    const [exportFormat, setExportFormat] = useState<string>('pdf');
    const [isExporting, setIsExporting] = useState<boolean>(false);

    const EXPORT_OPTIONS = ['PDF', 'PPT', 'CSV'];
    const EXPORT_VALUES = ['pdf', 'pptx', 'csv'];

    return (
      <>
        <div className={classes.topBar}>
          <div
            className={classes.edit}
            onClick={() => {
              navigate(COHORT_COMPARISON, {
                state: { triggerEdit: comparisonData },
              });
            }}
          >
            <Edit />
            <Typography
              fontSize="1rem"
              fontType="medium"
              textColor={patientAccessTheme.palette.text.highemphasis}
            >
              {cohortComparisonMessages.EDIT_ANALYSIS}
            </Typography>
          </div>

          <div className={classes.exportBar}>
            <Dropdown
              label="Export"
              dropDownOptions={EXPORT_OPTIONS}
              dropDownValues={EXPORT_VALUES}
              value={exportFormat}
              setValue={(value: string) => setExportFormat(value)}
              selectStyles={{
                ...commonDropDownProps.selectStyles,
                width: '75px',
                height: '25px',
              }}
              labelTop="10px"
              onChange={(event: any) =>
                setExportFormat(event.target.value as string)
              }
            />
            <Button
              onClick={async () => {
                setIsExporting(true);
                await handleBulkExport(exportFormat);
                setIsExporting(false);
              }}
              height="25px"
              variant="contained"
              label="Export"
              width="60px"
              disabled={isExporting || charts.every((c) => c === null)}
            />
          </div>
        </div>
        {isExporting && (
          <div className={classes.exportNote}>
            <div className={classes.exportText}>
              Please wait until the report is being downloaded
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Grid container className={classes.root}>
          <Grid className={classes.breadcrumbs}>
            <Breadcrumbs itemList={breadcrumbsItemsList} />
          </Grid>

          <Grid className={classes.content}>
            <div className={classes.stickyBar}>
              <ExportBar />
            </div>

            <div className={classes.divider} />

            <div className={classes.chartsArea}>
              <div style={{ width: '1200px' }}>
                {comparisonData?.trendList?.length > 0 ? (
                  comparisonData.trendList.map((trend: any, index: number) => {
                    const chart = charts[index];
                    return (
                      <Grid key={index}>
                        <div
                          ref={(el) => {
                            chartRefs.current[index] = el;
                          }}
                        >
                          <Grid className={classes.innerContainer}>
                            <Typography
                              fontSize="14px"
                              lineHeight="24px"
                              textColor={
                                patientAccessTheme.palette.text.highemphasis
                              }
                              fontType="medium"
                            >
                              {`Trend ${index + 1} - ${trend.trendName}`}
                            </Typography>
                          </Grid>

                          {chart ? (
                            <FusionCharts
                              typeOfChart={chart.type}
                              widthOfChart={1150}
                              heightOfChart={470}
                              dataSource={chart.dataSource}
                            />
                          ) : (
                            <CircularLoader />
                          )}
                        </div>
                      </Grid>
                    );
                  })
                ) : (
                  <NoDataVisual content="No Trends Analyzed" />
                )}
              </div>
            </div>
          </Grid>
        </Grid>
      )}
    </>
  );
});

export default CohortComparisonInternalPage;
